import { useState } from 'react';
import { trpc } from '@/lib/trpc';

interface Props {
  courseId: number;
}

/**
 * "Ask a question" panel — only rendered for course buyers
 * (parent component decides). Each user sees only their own questions.
 */
export default function CourseQuestions({ courseId }: Props) {
  const utils = trpc.useUtils();
  const { data: questions = [], isLoading } =
    trpc.questions.listMine.useQuery({ courseId });

  const [body, setBody] = useState('');

  const ask = trpc.questions.create.useMutation({
    onSuccess: () => {
      setBody('');
      utils.questions.listMine.invalidate({ courseId });
    },
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!body.trim()) return;
    ask.mutate({ courseId, body: body.trim() });
  };

  return (
    <section className="mt-6 bg-[#0A0A0B] border border-[#27272A] rounded-3xl p-6">
      <div className="flex items-baseline justify-between mb-3">
        <h3 className="text-lg font-bold text-white">Ask a question</h3>
        <span className="text-[#71717A] text-xs">private to instructor</span>
      </div>
      <p className="text-[#71717A] text-sm mb-4">
        Send a private question to the instructor. You'll see their reply here.
      </p>

      <form onSubmit={submit} className="space-y-3 mb-6">
        <textarea
          value={body}
          onChange={e => setBody(e.target.value)}
          placeholder="What would you like to ask?"
          rows={3}
          maxLength={2000}
          className="w-full bg-black border border-[#27272A] rounded-2xl px-4 py-3
                     text-sm text-white placeholder-[#52525B] outline-none
                     focus:border-yellow-400/40 resize-none"
        />
        {ask.error && (
          <p className="text-red-400 text-xs">{ask.error.message}</p>
        )}
        <button
          type="submit"
          disabled={ask.isPending || !body.trim()}
          className="mp-btn mp-btn-primary px-5 py-2 text-sm rounded-xl"
        >
          {ask.isPending ? 'Sending…' : 'Send question'}
        </button>
      </form>

      {isLoading ? (
        <p className="text-[#52525B] text-sm">Loading…</p>
      ) : questions.length === 0 ? (
        <p className="text-[#52525B] text-sm">No questions yet.</p>
      ) : (
        <ul className="space-y-4">
          {questions.map(q => (
            <li key={q.id} className="bg-black border border-[#27272A] rounded-2xl p-4">
              <div className="flex items-center justify-between mb-1">
                <span className="text-[#A1A1AA] text-xs font-semibold uppercase tracking-wide">
                  You asked
                </span>
                <span className="text-[#52525B] text-xs">
                  {new Date(q.createdAt).toLocaleDateString()}
                </span>
              </div>
              <p className="text-[#E4E4E7] text-sm whitespace-pre-wrap leading-relaxed">{q.body}</p>

              {q.reply ? (
                <div className="mt-3 pt-3 border-t border-white/5">
                  <div className="flex items-center justify-between mb-1">
                    <span className="text-yellow-400 text-xs font-semibold uppercase tracking-wide">
                      Instructor reply
                    </span>
                    {q.repliedAt && (
                      <span className="text-[#52525B] text-xs">
                        {new Date(q.repliedAt).toLocaleDateString()}
                      </span>
                    )}
                  </div>
                  <p className="text-[#E4E4E7] text-sm whitespace-pre-wrap leading-relaxed">{q.reply}</p>
                </div>
              ) : (
                <p className="mt-3 text-[#52525B] text-xs italic">Awaiting reply…</p>
              )}
            </li>
          ))}
        </ul>
      )}
    </section>
  );
}
